import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, formatDate } from "../../lib/api";
import { toast } from "sonner";
import { Zap, ShoppingBag, Wallet, TrendingUp, Lock, Plus, X, QrCode, Copy, ChevronRight, Clock, AlertTriangle, ShieldAlert, RotateCcw, Receipt, CheckCircle2, Send, User } from "lucide-react";
import EletrosReceipt from "./EletrosReceipt";

const PURPLE = "#2B1E4A";
const GOLD = "#F5B400";

export default function RevendaEletros() {
  const [access, setAccess] = useState(null);
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [resending, setResending] = useState({});
  const [sigFilter, setSigFilter] = useState("all");
  const filteredSales = sigFilter === "all" ? sales : sales.filter((s) => (s.signature_status || "PENDING_SIGNATURE") === sigFilter);

  const load = async () => {
    const a = await api.get("/eletros/me/access");
    setAccess(a.data);
    if (a.data.enabled) {
      const [s, sum] = await Promise.all([api.get("/eletros/sales"), api.get("/eletros/commissions/summary")]);
      setSales(s.data); setSummary(sum.data);
    }
  };
  useEffect(() => { load().catch(() => setAccess({ enabled: false })); }, []);

  const resendSignature = async (e, id) => {
    e.stopPropagation();
    if (resending[id]) return;
    setResending((p) => ({ ...p, [id]: true }));
    try {
      const { data } = await api.post(`/eletros/sales/${id}/resend-signature`);
      toast.success(data.message || "Solicitacao de assinatura reenviada pelo WhatsApp.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao reenviar assinatura");
    } finally {
      setTimeout(() => setResending((p) => ({ ...p, [id]: false })), 4000);
    }
  };

  if (!access) return <div className="p-6 text-sm text-[#5C5A57]">Carregando...</div>;

  if (!access.enabled) {
    return (
      <div className="grid min-h-[60vh] place-items-center p-6" data-testid="eletros-locked">
        <div className="max-w-sm rounded-2xl border border-border bg-white p-8 text-center">
          <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-[#F1ECF7]"><Lock size={26} className="text-[#5B2CA0]" /></div>
          <h2 className="font-display text-lg font-bold text-brand-dark">Eletros nao habilitado</h2>
          <p className="mt-2 text-sm text-[#6B5A69]">Sua conta ainda nao tem acesso a operacao Eletros. Fale com o administrador para habilitar.</p>
        </div>
      </div>
    );
  }

  const activeSales = sales.filter((s) => s.status !== "cancelled");
  const totalVendas = activeSales.reduce((a, s) => a + Number(s.total || 0), 0);
  const commissionPct = Math.round((access.commission_rate || 0) * 1000) / 10;

  return (
    <div className="space-y-6 p-4 lg:p-8" data-testid="revenda-eletros-page">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, #5B2CA0)` }}>
        <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: GOLD }}><Zap size={26} className="text-[#2B1E4A]" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Eletros</div>
          <h1 className="font-display text-xl font-bold">Minha operacao Eletros</h1>
          <p className="text-[12px] text-white/70">Comissao vigente: <span className="font-bold" style={{ color: GOLD }}>{commissionPct}%</span> · Ate {access.max_installments}x</p>
        </div>
        <Link to="/revenda/eletros/venda" data-testid="eletros-new-sale-btn" className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-[#2B1E4A]" style={{ background: GOLD }}>
          <Plus size={16} /> Nova venda
        </Link>
      </div>

      {/* Cards de comissao */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card icon={Clock} label="Comissao a receber" sub="projecao (nao e saldo)" value={brl(summary?.commission_projected)} />
        <Card icon={TrendingUp} label="Comissao gerada" sub="por pagamentos recebidos" value={brl(summary?.commission_generated)} gold />
        <Card icon={Wallet} label="Saldo liquido" sub={summary?.auto_compensate ? "comissoes - responsabilidades" : "so comissoes"} value={brl(summary?.available_balance)} gold negative={summary?.available_balance < 0} />
        <Card icon={ShoppingBag} label="Vendas / Total" sub={`${activeSales.length} vendas`} value={brl(totalVendas)} />
      </div>

      {/* Cards de responsabilidade */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Card icon={ShieldAlert} label="Responsabilidades pendentes" sub="principal assumido em aberto" value={brl(summary?.responsibility_pending)} danger={summary?.responsibility_pending > 0} />
        <Card icon={AlertTriangle} label="Total assumido" sub="por inadimplencia" value={brl(summary?.responsibility_assumed)} />
        <Card icon={RotateCcw} label="Total recuperado" sub="cliente pagou depois" value={brl(summary?.responsibility_recovered)} />
      </div>

      {/* Clientes proximos da responsabilidade */}
      {summary?.near_responsibility?.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="eletros-near-responsibility">
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-800"><AlertTriangle size={16} /> Clientes proximos da responsabilidade</div>
          <div className="space-y-1.5">
            {summary.near_responsibility.map((n) => (
              <div key={n.installment_id} className="flex items-center justify-between gap-2 rounded-lg bg-white p-2 text-[13px]" data-testid={`near-resp-${n.installment_id}`}>
                <span className="min-w-0 truncate text-brand-dark">
                  <span className="mr-1">{n.alert_level === "red" ? "🔴" : "🟠"}</span>
                  {n.client_name} · Parcela {n.number} · atraso {n.overdue_days}d
                </span>
                <span className="shrink-0 text-amber-700">em {n.days_to_responsibility}d vira sua responsabilidade · {brl(n.principal)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Link to="/revenda/eletros/compradores" data-testid="eletros-buyers-cta"
        className="flex items-center gap-3 rounded-2xl border border-[#E3D9F2] bg-gradient-to-r from-[#F5F1FB] to-white p-4 transition-colors hover:border-[#5B2CA0]">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-white" style={{ background: "#5B2CA0" }}><User size={22} /></span>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-[#2B1E4A]">Meus compradores</div>
          <div className="text-[12px] text-[#8A7788]">Ficha completa: compras, promissórias, pagamentos e recibos</div>
        </div>
        <ChevronRight size={20} className="shrink-0 text-[#5B2CA0]" />
      </Link>

      <div className="flex flex-wrap justify-end gap-3">
        <Link to="/revenda/eletros/conta" data-testid="eletros-conta-link" className="flex items-center gap-1 text-sm font-semibold text-[#5B2CA0] hover:underline">
          Conta corrente / saques <ChevronRight size={15} />
        </Link>
        <Link to="/revenda/eletros/comissoes" data-testid="eletros-commissions-link" className="flex items-center gap-1 text-sm font-semibold text-[#5B2CA0] hover:underline">
          Ver extrato de comissoes <ChevronRight size={15} />
        </Link>
      </div>

      <div className="rounded-2xl border border-border bg-white p-5" data-testid="eletros-sales-list">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-base font-bold text-brand-dark">Vendas recentes</h2>
          <div className="inline-flex rounded-lg border border-border p-0.5" data-testid="eletros-sig-filter">
            {[["all", "Todas"], ["PENDING_SIGNATURE", "Pendentes"], ["SIGNED", "Assinadas"]].map(([v, label]) => (
              <button key={v} onClick={() => setSigFilter(v)} data-testid={`eletros-sig-filter-${v}`}
                className={`rounded-md px-2.5 py-1 text-[11px] font-bold transition-colors ${sigFilter === v ? "bg-[#5B2CA0] text-white" : "text-[#6B5A69] hover:bg-muted"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {filteredSales.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#D9CFE8] bg-[#FAF8FD] p-8 text-center">
            <Zap size={26} className="mx-auto mb-2 text-[#5B2CA0]" />
            <p className="text-sm text-[#6B5A69]">{sales.length === 0 ? 'Nenhuma venda Eletros ainda. Toque em "Nova venda" para comecar.' : "Nenhuma venda neste filtro."}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredSales.map((s) => (
              <button key={s.id} onClick={() => setDetail(s.id)} className="flex w-full items-center justify-between gap-3 rounded-xl border border-border p-3 text-left hover:border-[#5B2CA0]" data-testid={`eletros-sale-row-${s.id}`}>
                <div className="min-w-0">
                  <div className="truncate font-medium text-brand-dark">{s.client_name}</div>
                  <div className="text-[12px] text-[#8A7788]">{formatDate(s.created_at)} · {s.items?.length || 0} item(ns) · {s.payment_method === "cash" ? "A vista" : `${s.installments_count}x`}</div>
                  {s.status !== "cancelled" && <div className="mt-1"><SignatureBadge status={s.signature_status} /></div>}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <div className="font-bold text-brand-dark">{brl(s.total)}</div>
                    {s.status === "cancelled" && <span className="text-[10px] font-bold uppercase text-red-500">Cancelada</span>}
                  </div>
                  {s.status !== "cancelled" && s.signature_status !== "SIGNED" && (
                    <span onClick={(e) => resendSignature(e, s.id)} data-testid={`eletros-resend-sign-${s.id}`} title="Reenviar assinatura pelo WhatsApp"
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg border text-[#B45309] ${resending[s.id] ? "opacity-40" : "hover:bg-amber-50"}`} style={{ borderColor: "#F5B400" }}>
                      <Send size={14} />
                    </span>
                  )}
                  <span onClick={(e) => { e.stopPropagation(); setReceipt(s.id); }} data-testid={`eletros-receipt-btn-${s.id}`} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border text-[#5B2CA0] hover:bg-[#F1ECF7]" title="Recibo / compartilhar"><Receipt size={15} /></span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {detail && <SaleFinanceModal saleId={detail} onClose={() => setDetail(null)} onChanged={load} />}
      {receipt && <EletrosReceipt saleId={receipt} onClose={() => setReceipt(null)} />}
    </div>
  );
}

function Card({ icon: Icon, label, sub, value, gold, danger, negative }) {
  const color = negative ? "#DC2626" : danger ? "#DC2626" : gold ? "#C9960A" : PURPLE;
  return (
    <div className="rounded-2xl border border-border bg-white p-4" data-testid={`eletros-card-${label}`}>
      <Icon size={18} className={danger || negative ? "text-red-500" : gold ? "text-[#C9960A]" : "text-[#5B2CA0]"} />
      <div className="mt-2 text-[10px] uppercase tracking-widest text-[#9A8A98]">{label}</div>
      <div className="font-display text-lg font-bold" style={{ color }}>{value}</div>
      <div className="text-[10px] text-[#9A8A98]">{sub}</div>
    </div>
  );
}

function ReminderHistory({ rem }) {
  const count = (rem && rem.count) || 0;
  const log = (rem && rem.log) || [];
  const last = log.length ? log[log.length - 1] : null;
  return (
    <div className="mb-3 rounded-xl border border-border p-3" data-testid="eletros-reminder-history">
      <div className="text-[11px] font-bold uppercase tracking-widest text-[#8A7788]">Lembretes de assinatura</div>
      <div className="mt-1 text-sm text-brand-dark">
        {count === 0 ? "Nenhum lembrete enviado"
          : `${count} lembrete(s) enviado(s)${last ? ` · ultimo em ${formatDate(last.at)}` : ""}`}
      </div>
      {log.length > 0 && (
        <ul className="mt-2 space-y-1">
          {log.slice(-5).reverse().map((e, i) => (
            <li key={i} className="flex items-center justify-between text-[12px] text-[#6B5A69]">
              <span>{formatDate(e.at)} · {e.type === "AUTOMATIC" ? "Automatico" : "Manual"} · {e.channel}</span>
              <span className={e.result === "sent" ? "text-emerald-600 font-semibold" : "text-red-500 font-semibold"}>{e.result === "sent" ? "enviado" : "falhou"}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function SignatureBadge({ status }) {
  if (status === "SIGNED")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700" data-testid="eletros-sig-signed">
        <CheckCircle2 size={11} /> Promissoria assinada ✓
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "#FEF3C7", color: "#B45309" }} data-testid="eletros-sig-pending">
      <Clock size={11} /> Promissoria pendente
    </span>
  );
}

function SaleFinanceModal({ saleId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [pix, setPix] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  const load = async () => setData((await api.get(`/eletros/sales/${saleId}/finance`)).data);
  useEffect(() => { load(); /* eslint-disable-line */ }, [saleId]);
  const [receipts, setReceipts] = useState([]);
  useEffect(() => { api.get(`/eletros/sales/${saleId}/receipts`).then((r) => setReceipts(r.data || [])).catch(() => setReceipts([])); }, [saleId]);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const resend = async () => {
    if (resending) return;
    setResending(true);
    try { const { data } = await api.post(`/eletros/sales/${saleId}/resend-signature`); toast.success(data.message || "Reenviado pelo WhatsApp."); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao reenviar"); }
    finally { setTimeout(() => setResending(false), 4000); }
  };

  const genCharge = async (instId) => {
    setBusy(true);
    try { const { data } = await api.post(`/eletros/installments/${instId}/charge`); setPix(data); toast.success("Cobranca PIX gerada"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar cobranca"); }
    finally { setBusy(false); }
  };

  const STATUS = { paid: ["Paga", "text-emerald-600"], partial: ["Parcial", "text-amber-600"], partial_late: ["Parcial (atraso)", "text-amber-700"], late: ["Em atraso", "text-red-600"], pending: ["Pendente", "text-[#6B5A69]"], cancelled: ["Cancelada", "text-gray-400"] };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-end bg-black/50 sm:place-items-center sm:p-6" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white p-5 shadow-pop sm:rounded-3xl sm:p-6" onClick={(e) => e.stopPropagation()} data-testid="eletros-sale-finance-modal">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-brand-dark">Detalhe financeiro da venda</h2>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted"><X size={18} /></button>
        </div>
        {!data ? <div className="py-8 text-center text-sm text-[#9A8A98]">Carregando...</div> : (
          <>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#FBFAFE] p-3">
              <div className="flex items-center gap-2">
                <SignatureBadge status={data.sale.signature_status} />
                {data.sale.signature_status === "SIGNED" && data.promissory?.id && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/promissories/${data.promissory.id}/pdf`} target="_blank" rel="noopener noreferrer" className="text-[11px] font-semibold text-[#5B2CA0] hover:underline" data-testid="eletros-modal-pdf-link">Ver PDF</a>
                )}
              </div>
              {data.sale.status !== "cancelled" && data.sale.signature_status !== "SIGNED" && (
                <button onClick={resend} disabled={resending} data-testid="eletros-modal-resend-sign" className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-[12px] font-bold text-[#2B1E4A] disabled:opacity-50" style={{ background: "#F5B400" }}>
                  <Send size={13} /> {resending ? "Reenviando..." : "Reenviar assinatura"}
                </button>
              )}
            </div>
            <ReminderHistory rem={data.sale.signature_reminders} />

            <div className="mb-3 flex flex-wrap justify-between gap-3 rounded-xl bg-muted/50 p-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Cliente</div><div className="font-semibold text-brand-dark">{data.sale.client_name}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Comissao gerada</div><div className="font-semibold text-[#C9960A]">{brl(data.commission_generated)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Comissao projetada</div><div className="font-semibold text-[#5B2CA0]">{brl(data.commission_projected)}</div></div>
            </div>
            <div className="space-y-2">
              {data.installments.map((it) => {
                const [label, cls] = STATUS[it.status] || STATUS.pending;
                return (
                  <div key={it.id} className="rounded-xl border border-border p-3" data-testid={`eletros-inst-${it.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-brand-dark">Parcela {it.number} <span className="text-[11px] font-normal text-[#8A7788]">· venc. {formatDate(it.due_date)}</span></div>
                      <span className={`text-[11px] font-bold ${cls}`}>{label}</span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] sm:grid-cols-3">
                      <Row k="Valor original" v={brl(it.amount)} />
                      <Row k="Principal pago" v={brl(it.paid_total)} />
                      <Row k="Encargos" v={brl(it.fine + it.interest)} />
                      <Row k="Saldo (atualizado)" v={brl(it.open_amount)} />
                      <Row k="Comissao gerada" v={brl(it.commission_generated)} gold />
                      <Row k="Comissao projetada" v={brl(it.commission_projected)} />
                      {it.overdue_days > 0 && <Row k="Dias em atraso" v={`${it.overdue_days}d`} />}
                      {it.last_payment_at && <Row k="Ultimo pagamento" v={formatDate(it.last_payment_at)} />}
                    </div>
                    {receipts.filter((r) => r.installment_id === it.id).length > 0 && (
                      <div className="mt-2 rounded-lg bg-[#FBFAFE] p-2" data-testid={`eletros-receipts-${it.id}`}>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[#8A7788] mb-1">Comprovantes / recibos</div>
                        <div className="space-y-1">
                          {receipts.filter((r) => r.installment_id === it.id).map((r) => (
                            <div key={r.id} className="flex items-center justify-between gap-2 text-[12px]" data-testid={`eletros-receipt-row-${r.id}`}>
                              <span className="text-brand-dark">
                                {r.kind === "full" ? "Comprovante" : "Recibo parcial"} · {brl(r.amount_paid)}
                                {r.status === "reversed" && <span className="ml-1 rounded bg-red-100 px-1 text-[10px] font-bold text-red-700">ESTORNADO</span>}
                              </span>
                              <a href={`${BACKEND}/api/eletros/receipts/${r.token}`} target="_blank" rel="noopener noreferrer"
                                 className="flex items-center gap-1 font-semibold text-[#5B2CA0] hover:underline" data-testid={`eletros-receipt-link-${r.id}`}>
                                <Receipt size={12} /> Ver recibo
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {it.responsibility_status !== "cliente" && (
                      <div className="mt-2 rounded-lg bg-red-50 p-2 text-[12px]" data-testid={`eletros-resp-${it.id}`}>
                        <div className="flex items-center gap-1 font-semibold text-red-700"><ShieldAlert size={13} /> Responsabilidade {it.responsibility_status === "recuperada" ? "recuperada" : "assumida"}</div>
                        <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-red-800/90">
                          <Row k="Transferida em" v={it.responsibility_transferred_at ? formatDate(it.responsibility_transferred_at) : "—"} />
                          <Row k="Valor transferido" v={brl(it.responsibility_amount)} />
                          <Row k="Ja recuperado" v={brl(it.responsibility_recovered)} />
                          <Row k="Sob responsabilidade" v={brl(it.responsibility_outstanding)} />
                        </div>
                      </div>
                    )}
                    {it.alert_level && (
                      <div className="mt-2 text-[12px] font-medium" data-testid={`eletros-alert-${it.id}`}>
                        {it.alert_level === "red" ? "🔴" : it.alert_level === "orange" ? "🟠" : "🟡"} {it.days_to_responsibility != null ? `Em ${it.days_to_responsibility} dia(s) este saldo pode passar para sua responsabilidade.` : "Parcela em atraso."}
                      </div>
                    )}
                    {it.status !== "paid" && it.status !== "cancelled" && (
                      <button onClick={() => genCharge(it.id)} disabled={busy} data-testid={`eletros-gen-charge-${it.id}`} className="mt-2 flex items-center gap-1 rounded-lg border border-[#5B2CA0] px-3 py-1.5 text-[12px] font-semibold text-[#5B2CA0] disabled:opacity-50">
                        <QrCode size={13} /> Gerar cobranca PIX
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {pix && (
              <div className="mt-4 rounded-xl border border-[#5B2CA0] bg-[#F1ECF7] p-4 text-center" data-testid="eletros-pix-box">
                <div className="text-[11px] font-bold uppercase text-[#5B2CA0]">PIX gerado ({brl(pix.amount)})</div>
                {pix.qr_code_data_url && <img src={pix.qr_code_data_url} alt="QR" className="mx-auto my-2 h-40 w-40" />}
                {pix.copy_paste && (
                  <button onClick={() => { navigator.clipboard.writeText(pix.copy_paste); toast.success("Copia e cola copiado"); }} className="mx-auto flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-[12px] font-semibold text-[#5B2CA0]">
                    <Copy size={13} /> Copiar codigo PIX
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, gold }) {
  return <div className="flex justify-between gap-2"><span className="text-[#8A7788]">{k}</span><span className={`font-medium ${gold ? "text-[#C9960A]" : "text-brand-dark"}`}>{v}</span></div>;
}
